import React, { useState } from 'react';
import {
  Shield,
  Eye,
  Brain,
  Link2,
  Trash2,
  Download,
  AlertCircle,
  Info
} from 'lucide-react';
import { Button, Card } from '../ui';
import './PrivacySettings.css';

interface PrivacySettingsProps {
  initialSettings?: AIPrivacySettings;
  onSave?: (settings: AIPrivacySettings) => void;
  isPremium?: boolean;
}

export interface AIPrivacySettings {
  enableAIAnalysis: boolean;
  enableOCR: boolean;
  shareWithAI: boolean;
  includeAppDataInAnalysis: boolean;
  retainAnalysisHistory: boolean;
  autoDeleteAfterDays: number | null;
}

const defaultSettings: AIPrivacySettings = {
  enableAIAnalysis: true,
  enableOCR: true,
  shareWithAI: true,
  includeAppDataInAnalysis: true,
  retainAnalysisHistory: true,
  autoDeleteAfterDays: null,
};

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({
  initialSettings = defaultSettings,
  onSave,
  isPremium = false,
}) => {
  const [settings, setSettings] = useState<AIPrivacySettings>(initialSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleToggle = (key: keyof AIPrivacySettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  const handleAutoDeleteChange = (days: number | null) => {
    setSettings((prev) => ({
      ...prev,
      autoDeleteAfterDays: days,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave?.(settings);
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAllData = async () => {
    // This would call the API to delete all AI analysis data
    setShowDeleteConfirm(false);
  };

  if (!isPremium) {
    return (
      <Card variant="elevated" padding="lg" className="privacy-settings privacy-settings--locked">
        <div className="privacy-settings__locked-icon">
          <Shield size={48} />
        </div>
        <h3>Premium Feature</h3>
        <p>AI Analysis privacy settings are available for Premium subscribers.</p>
        <Button variant="primary">Upgrade to Premium</Button>
      </Card>
    );
  }

  return (
    <div className="privacy-settings">
      <div className="privacy-settings__header">
        <div className="privacy-settings__title-section">
          <Shield size={24} />
          <div>
            <h2>AI Analysis Privacy</h2>
            <p>Control how your morning pages are analyzed and stored</p>
          </div>
        </div>
        {hasChanges && (
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving}
          >
            Save Changes
          </Button>
        )}
      </div>

      <div className="privacy-settings__info">
        <Info size={16} />
        <p>
          Your morning pages content is processed securely and never shared with third parties.
          AI analysis is performed using encrypted connections and your data is only used to
          provide personalized insights.
        </p>
      </div>

      {/* Core Settings */}
      <Card variant="outlined" padding="md" className="privacy-settings__section">
        <h3 className="privacy-settings__section-title">
          <Brain size={18} />
          AI Analysis Features
        </h3>

        <div className="privacy-settings__option">
          <div className="privacy-settings__option-info">
            <span className="privacy-settings__option-label">Enable AI Analysis</span>
            <span className="privacy-settings__option-description">
              Allow AI to analyze your morning pages for themes, emotions, and patterns
            </span>
          </div>
          <button
            className={`privacy-settings__toggle ${settings.enableAIAnalysis ? 'privacy-settings__toggle--on' : ''}`}
            onClick={() => handleToggle('enableAIAnalysis')}
            role="switch"
            aria-checked={settings.enableAIAnalysis}
          >
            <span className="privacy-settings__toggle-track" />
            <span className="privacy-settings__toggle-thumb" />
          </button>
        </div>

        <div className="privacy-settings__option">
          <div className="privacy-settings__option-info">
            <span className="privacy-settings__option-label">Enable OCR Text Extraction</span>
            <span className="privacy-settings__option-description">
              Extract text from handwritten morning pages using optical character recognition
            </span>
          </div>
          <button
            className={`privacy-settings__toggle ${settings.enableOCR ? 'privacy-settings__toggle--on' : ''}`}
            onClick={() => handleToggle('enableOCR')}
            role="switch"
            aria-checked={settings.enableOCR}
          >
            <span className="privacy-settings__toggle-track" />
            <span className="privacy-settings__toggle-thumb" />
          </button>
        </div>
      </Card>

      {/* Data Sharing */}
      <Card variant="outlined" padding="md" className="privacy-settings__section">
        <h3 className="privacy-settings__section-title">
          <Link2 size={18} />
          Data Integration
        </h3>

        <div className="privacy-settings__option">
          <div className="privacy-settings__option-info">
            <span className="privacy-settings__option-label">Include App Data in Analysis</span>
            <span className="privacy-settings__option-description">
              Correlate morning pages with your projects, flow sessions, and check-ins for deeper insights
            </span>
          </div>
          <button
            className={`privacy-settings__toggle ${settings.includeAppDataInAnalysis ? 'privacy-settings__toggle--on' : ''}`}
            onClick={() => handleToggle('includeAppDataInAnalysis')}
            role="switch"
            aria-checked={settings.includeAppDataInAnalysis}
            disabled={!settings.enableAIAnalysis}
          >
            <span className="privacy-settings__toggle-track" />
            <span className="privacy-settings__toggle-thumb" />
          </button>
        </div>
      </Card>

      {/* Data Retention */}
      <Card variant="outlined" padding="md" className="privacy-settings__section">
        <h3 className="privacy-settings__section-title">
          <Eye size={18} />
          Data Retention
        </h3>

        <div className="privacy-settings__option">
          <div className="privacy-settings__option-info">
            <span className="privacy-settings__option-label">Retain Analysis History</span>
            <span className="privacy-settings__option-description">
              Keep a history of all AI-generated insights and weekly summaries
            </span>
          </div>
          <button
            className={`privacy-settings__toggle ${settings.retainAnalysisHistory ? 'privacy-settings__toggle--on' : ''}`}
            onClick={() => handleToggle('retainAnalysisHistory')}
            role="switch"
            aria-checked={settings.retainAnalysisHistory}
          >
            <span className="privacy-settings__toggle-track" />
            <span className="privacy-settings__toggle-thumb" />
          </button>
        </div>

        <div className="privacy-settings__option privacy-settings__option--column">
          <div className="privacy-settings__option-info">
            <span className="privacy-settings__option-label">Auto-Delete OCR Text</span>
            <span className="privacy-settings__option-description">
              Automatically delete extracted text after a certain period
            </span>
          </div>
          <div className="privacy-settings__auto-delete">
            {[
              { value: null, label: 'Never' },
              { value: 30, label: '30 days' },
              { value: 90, label: '90 days' },
              { value: 180, label: '6 months' },
              { value: 365, label: '1 year' },
            ].map(({ value, label }) => (
              <button
                key={label}
                className={`privacy-settings__auto-delete-btn ${settings.autoDeleteAfterDays === value ? 'privacy-settings__auto-delete-btn--active' : ''}`}
                onClick={() => handleAutoDeleteChange(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Data Management */}
      <Card variant="outlined" padding="md" className="privacy-settings__section privacy-settings__section--danger">
        <h3 className="privacy-settings__section-title">
          <AlertCircle size={18} />
          Data Management
        </h3>

        <div className="privacy-settings__actions">
          <Button variant="secondary" size="md">
            <Download size={16} />
            Export My Data
          </Button>

          <Button
            variant="danger"
            size="md"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 size={16} />
            Delete All AI Data
          </Button>
        </div>

        <p className="privacy-settings__warning">
          Deleting AI data will remove all extracted text, insights, and weekly summaries.
          Your original morning page photos will not be affected.
        </p>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="privacy-settings__modal-backdrop" onClick={() => setShowDeleteConfirm(false)}>
          <Card
            variant="elevated"
            padding="lg"
            className="privacy-settings__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="privacy-settings__modal-icon">
              <AlertCircle size={48} />
            </div>
            <h3>Delete All AI Analysis Data?</h3>
            <p>
              This will permanently delete all OCR text, insights, and weekly summaries.
              This action cannot be undone.
            </p>
            <div className="privacy-settings__modal-actions">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteAllData}>
                Yes, Delete Everything
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrivacySettings;
